import { useState, type KeyboardEvent, type ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  columnFilteringFeature,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  functionalUpdate,
  globalFilteringFeature,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable,
  type ColumnDef,
  type HeaderGroup,
  type PaginationState,
  type ReactTable,
  type Row,
  type RowData,
  type SortingState,
  type Updater,
} from "@tanstack/react-table";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pager } from "@/components/ui/pager";
import {
  Skeleton,
  SkeletonLine,
  SkeletonRegion,
} from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@jbnl/shared-ui";

/**
 * Client-side sorting, search and paging: the shape every list here arrives in. A set too large
 * to hold whole moves to `serverDriven` and leaves everything below untouched.
 */
export const dataTableFeatures = tableFeatures({
  columnFilteringFeature,
  filteredRowModel: createFilteredRowModel(),
  globalFilteringFeature,
  paginatedRowModel: createPaginatedRowModel(),
  rowPaginationFeature,
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  filterFns: { includesString: filterFn_includesString },
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
});

export type DataTableColumns<TData extends RowData> = Array<
  ColumnDef<typeof dataTableFeatures, TData>
>;

interface Props<TData extends RowData> {
  /** Names the grid for a screen reader, and labels its search box. */
  label: string;
  data: TData[];
  columns: DataTableColumns<TData>;
  /** The domain id, never the row index - the index changes when the sort does. */
  getRowId: (row: TData) => string;
  /** The record the detail pane is showing. */
  selectedId?: string;
  onSelect: (id: string) => void;
  /** Placeholder for the search box. Left out, the table has no search box. */
  searchPlaceholder?: string;
  /** What to say when there is nothing to choose from at all. */
  empty: ReactNode;
  /** The list's `/export` route with its filters and sort. Without it, the table shows no export link. */
  exportHref?: string;
  /**
   * One page of an ordering the table did not compute, so sorting it again client-side would
   * fight the server. Also lifts sorting and the search box into the caller's state.
   */
  serverDriven?: {
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
    globalFilter: string;
    onGlobalFilterChange: (value: string) => void;
  };
  /**
   * Windows an already-fetched `data`, composing with client-side sorting and search. Mutually
   * exclusive with `serverDriven`, whose page the caller already fetched.
   */
  clientPageSize?: number;
  /**
   * The current page (1-based) for `clientPageSize` mode, and where to send the next one -
   * e.g. a route search param, so the page survives a reload or a shared link. Left out, the
   * page is local state that resets on remount.
   */
  page?: number;
  onPageChange?: (page: number) => void;
  /** `full` numbered pages, or `compact` prev/next-only - see `Pager`'s own doc comment. */
  pagerVariant?: "full" | "compact";
}

/**
 * A list of records to pick one out of. The row is the control - no cell holds a second one -
 * so the table is an ARIA grid with a roving tabindex rather than a table of buttons.
 */
export function DataTable<TData extends RowData>({
  label,
  data,
  columns,
  getRowId,
  selectedId,
  onSelect,
  searchPlaceholder,
  empty,
  exportHref,
  serverDriven,
  clientPageSize,
  page,
  onPageChange,
  pagerVariant = "full",
}: Props<TData>) {
  const [localGlobalFilter, setLocalGlobalFilter] = useState("");
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const [localPageIndex, setLocalPageIndex] = useState(0);

  const globalFilter = serverDriven?.globalFilter ?? localGlobalFilter;
  const sorting = serverDriven?.sorting ?? localSorting;
  const pageIndex = (page ?? localPageIndex + 1) - 1;
  const setPageIndex = (index: number) =>
    onPageChange ? onPageChange(index + 1) : setLocalPageIndex(index);
  // A page size large enough to never actually paginate, for every table that isn't in
  // `clientPageSize` mode - `getRowModel()` then still returns everything, unpaginated, exactly
  // as it did before this feature existed.
  const pageSize = clientPageSize ?? Number.MAX_SAFE_INTEGER;

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns,
    getRowId,
    globalFilterFn: filterFn_includesString,
    state: { globalFilter, sorting, pagination: { pageIndex, pageSize } },
    // TanStack Table calls these with either a value or a `(previous) => next` updater - the
    // header click handler uses the updater form. `serverDriven`'s callbacks take a plain
    // value, so an updater is resolved against the current state before it reaches them.
    onGlobalFilterChange: (updater: Updater<string>) => {
      // A search narrows the set a client-paginated table windows, so page 2 of the old set
      // would otherwise land past the end of the new, shorter one.
      if (clientPageSize) setPageIndex(0);
      return serverDriven
        ? serverDriven.onGlobalFilterChange(
            functionalUpdate(updater, globalFilter),
          )
        : setLocalGlobalFilter(updater);
    },
    onSortingChange: (updater: Updater<SortingState>) =>
      serverDriven
        ? serverDriven.onSortingChange(functionalUpdate(updater, sorting))
        : setLocalSorting(updater),
    onPaginationChange: (updater: Updater<PaginationState>) =>
      setPageIndex(
        functionalUpdate(updater, { pageIndex, pageSize }).pageIndex,
      ),
    manualSorting: Boolean(serverDriven),
    manualFiltering: Boolean(serverDriven),
  });

  const rows = table.getRowModel().rows;
  const totalRowCount = table.getSortedRowModel().rows.length;

  // In `serverDriven` mode `data` is already one filtered page, so an empty page during an
  // active search means "nothing matched" - handled below, beside the search box - and not
  // "there is nothing to choose from at all".
  if (isEmptyDataset(data.length, serverDriven, globalFilter)) {
    return <div className="text-sm text-muted-foreground">{empty}</div>;
  }

  return (
    <div className="space-y-3">
      {(searchPlaceholder || exportHref) && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Zoekveld
              label={label}
              searchPlaceholder={searchPlaceholder}
              globalFilter={globalFilter}
              onChange={(value) =>
                serverDriven
                  ? serverDriven.onGlobalFilterChange(value)
                  : setLocalGlobalFilter(value)
              }
            />
          </div>
          {exportHref && <ExportLink href={exportHref} />}
        </div>
      )}

      <div className="overflow-hidden rounded-md border bg-background">
        <Table role="grid" aria-label={label}>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <DataTableHeaderRow key={group.id} group={group} table={table} />
            ))}
          </TableHeader>

          <TableBody>
            <DataTableRows
              table={table}
              rows={rows}
              globalFilter={globalFilter}
              onSelect={onSelect}
              selectedId={selectedId}
            />
          </TableBody>
        </Table>
      </div>

      {clientPageSize && (
        <Pager
          page={pageIndex + 1}
          pageSize={clientPageSize}
          totalCount={totalRowCount}
          onPageChange={(page) => setPageIndex(page - 1)}
          variant={pagerVariant}
        />
      )}
    </div>
  );
}

function isEmptyDataset(
  dataLength: number,
  serverDriven: unknown,
  globalFilter: string,
) {
  return dataLength === 0 && !(serverDriven && globalFilter);
}

function Zoekveld({
  label,
  searchPlaceholder,
  globalFilter,
  onChange,
}: {
  label: string;
  searchPlaceholder?: string;
  globalFilter: string;
  onChange: (value: string) => void;
}) {
  if (!searchPlaceholder) return null;

  return (
    <div className="grid gap-2">
      <Label htmlFor={`${label}-zoeken`} className="sr-only">
        {label} zoeken
      </Label>
      <Input
        id={`${label}-zoeken`}
        type="search"
        placeholder={searchPlaceholder}
        value={globalFilter}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function ExportLink({ href }: { href: string }) {
  const { t } = useTranslation();

  return (
    <a href={href} download className={buttonVariants({ variant: "outline" })}>
      <Download aria-hidden="true" />
      {t("table.export")}
    </a>
  );
}

function ariaSortValue(sorted: false | "asc" | "desc") {
  return sorted === "asc"
    ? "ascending"
    : sorted === "desc"
      ? "descending"
      : "none";
}

function SortIcon({ sorted }: { sorted: false | "asc" | "desc" }) {
  if (sorted === "asc") return <ArrowUp className="size-3" />;
  if (sorted === "desc") return <ArrowDown className="size-3" />;
  return <ChevronsUpDown className="size-3 opacity-40" />;
}

function DataTableHeaderRow<TData extends RowData>({
  group,
  table,
}: {
  group: HeaderGroup<typeof dataTableFeatures, TData>;
  table: ReactTable<typeof dataTableFeatures, TData>;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      {group.headers.map((header) => {
        const sorted = header.column.getIsSorted();

        return (
          <TableHead key={header.id} aria-sort={ariaSortValue(sorted)}>
            {header.isPlaceholder ? null : header.column.getCanSort() ? (
              <button
                type="button"
                className="flex items-center gap-1 rounded-sm uppercase focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
                onClick={header.column.getToggleSortingHandler()}
              >
                <table.FlexRender header={header} />
                <SortIcon sorted={sorted} />
              </button>
            ) : (
              <table.FlexRender header={header} />
            )}
          </TableHead>
        );
      })}
    </TableRow>
  );
}

function DataTableRows<TData extends RowData>({
  table,
  rows,
  globalFilter,
  onSelect,
  selectedId,
}: {
  table: ReactTable<typeof dataTableFeatures, TData>;
  rows: Row<typeof dataTableFeatures, TData>[];
  globalFilter: string;
  onSelect: (id: string) => void;
  selectedId?: string;
}) {
  if (rows.length === 0) {
    return (
      <TableRow className="hover:bg-transparent">
        <TableCell
          colSpan={table.getAllLeafColumns().length}
          className="py-6 text-center text-sm text-muted-foreground"
        >
          Niets gevonden voor &ldquo;{globalFilter}&rdquo;.
        </TableCell>
      </TableRow>
    );
  }

  const selectedIndex = rows.findIndex((row) => row.id === selectedId);
  // Roving tabindex: the grid is one tab stop, and the arrow keys walk it. Without this a
  // korpsenlijst puts forty stops between the search box and the detail pane.
  const focusIndex = selectedIndex === -1 ? 0 : selectedIndex;

  return (
    <>
      {rows.map((row, index) => (
        <DataTableRow
          key={row.id}
          table={table}
          row={row}
          index={index}
          focusIndex={focusIndex}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </>
  );
}

function DataTableRow<TData extends RowData>({
  table,
  row,
  index,
  focusIndex,
  onSelect,
  selectedId,
}: {
  table: ReactTable<typeof dataTableFeatures, TData>;
  row: Row<typeof dataTableFeatures, TData>;
  index: number;
  focusIndex: number;
  onSelect: (id: string) => void;
  selectedId?: string;
}) {
  return (
    <TableRow
      tabIndex={index === focusIndex ? 0 : -1}
      aria-selected={row.id === selectedId}
      data-state={row.id === selectedId ? "selected" : undefined}
      // Blue, as everywhere a selected state is drawn here: red is the action
      // colour and a highlighted row is not an action.
      className={cn(
        "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
        "data-[state=selected]:bg-secondary-surface data-[state=selected]:hover:bg-secondary-surface",
      )}
      onClick={() => onSelect(row.id)}
      onKeyDown={(event) => onRowKeyDown(event, () => onSelect(row.id))}
    >
      {row.getAllCells().map((cell) => (
        <TableCell key={cell.id}>
          <table.FlexRender cell={cell} />
        </TableCell>
      ))}
    </TableRow>
  );
}

/** Moves focus to the previous/next row, the roving-tabindex grid's own arrow-key navigation. */
function focusSiblingRow(row: HTMLTableRowElement, direction: 1 | -1) {
  const siblings = Array.from(row.parentElement?.children ?? []);
  const next = siblings[siblings.indexOf(row) + direction];
  if (next instanceof HTMLElement) next.focus();
}

function onRowKeyDown(
  event: KeyboardEvent<HTMLTableRowElement>,
  select: () => void,
) {
  if (["Enter", " "].includes(event.key)) {
    event.preventDefault();
    select();
    return;
  }

  if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;

  event.preventDefault();
  focusSiblingRow(event.currentTarget, event.key === "ArrowDown" ? 1 : -1);
}

/**
 * Takes the caller's own `columns` rather than header strings, so the widths cannot drift. The
 * headers are drawn for real: they are chrome, and already known while the rows are in flight.
 */
export function DataTableSkeleton<TData extends RowData>({
  label,
  columns,
  rows = 5,
  search = false,
  twoLine = [],
  busy,
}: {
  label: string;
  columns: DataTableColumns<TData>;
  /** How many rows to draw. Roughly what the list usually holds, never more than fills a pane. */
  rows?: number;
  /** Draw the search box, where the real table has one. */
  search?: boolean;
  /** Ids of the columns whose cell is a title with a smaller second line under it. */
  twoLine?: string[];
  /** What a screen reader hears while the rows are on their way. Defaults to the catalog's. */
  busy?: string;
}) {
  const { t } = useTranslation();

  const table = useTable({
    features: dataTableFeatures,
    data: [] as TData[],
    columns,
  });

  const headers = table.getHeaderGroups()[0]?.headers ?? [];

  return (
    <SkeletonRegion label={busy ?? t("table.busy")} className="space-y-3">
      {search && <Skeleton className="h-9 w-full" />}

      <div className="overflow-hidden rounded-md border bg-background">
        <Table aria-label={label}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder ? null : (
                    <table.FlexRender header={header} />
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {Array.from({ length: rows }, (_, row) => (
              <TableRow key={row} className="hover:bg-transparent">
                {headers.map((header, column) => (
                  <TableCell key={header.id}>
                    {/* Fixed widths, not a fraction of the cell: with no text in it a
                        percentage-sized bar gives the column no width to be a fraction of. */}
                    <span className="block">
                      <SkeletonLine
                        className={column === 0 ? "w-40" : "w-24"}
                      />
                      {twoLine.includes(header.column.id) && (
                        <SkeletonLine size="xs" className="w-28" />
                      )}
                    </span>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SkeletonRegion>
  );
}
